import React from "react";
import { fromPairs } from "lodash";
import { useObserver } from "mobx-react-lite";
import { subDays, eachDayOfInterval, isAfter } from "date-fns";
import { useHistory, useParams } from "react-router";
import { Grid, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import { Formatter } from "libs";
import { DatePicker, Table } from "components";
import { StoreProvider, useStore } from "./Store";

const useStyles = makeStyles((theme) => ({
  date: {
    marginRight: theme.spacing(2),
  },
}));

interface Params {
  developerId: string;
  appId: string;
  destinationId: string;
}

export const Page = () => {
  const store = useStore();
  const history = useHistory();
  const params = useParams<Params>();
  const styles = useStyles();

  React.useEffect(() => {
    store.fetchRetentionReports(+params.developerId, +params.appId);
  }, [params.appId, params.developerId, store]);

  const handleStartAt = React.useCallback(
    (date: Date | null) => {
      if (!date) return;
      store.startAt = date;
      store.fetchRetentionReports(+params.developerId, +params.appId);
    },
    [params.appId, params.developerId, store]
  );

  const handleEndAt = React.useCallback(
    (date: Date | null) => {
      if (!date) return;
      store.endAt = date;
      store.fetchRetentionReports(+params.developerId, +params.appId);
    },
    [params.appId, params.developerId, store]
  );

  const data = React.useMemo(
    () =>
      eachDayOfInterval({
        start: store.startAt,
        end: store.endAt,
      })
        .reverse()
        .map((date) => ({
          date,
          ...fromPairs(
            [0, 1, 3, 7, 14, 30].map((days) => {
              if (isAfter(date, subDays(new Date(), days + 1))) {
                return [days, undefined];
              }
              const report = store.retentionReports.find(
                (report) =>
                  report.destination === +params.destinationId &&
                  report.date === Formatter.date(date) &&
                  report.days === days
              );
              return [days, Formatter.number(report && report.uu)];
            })
          ),
        })),
    [params.destinationId, store.endAt, store.retentionReports, store.startAt]
  );

  return useObserver(() => (
    <Grid container spacing={2} justify="space-between" alignItems="center">
      <Grid item xs="auto">
        <IconButton color="primary" onClick={history.goBack}>
          <ArrowBackIcon />
        </IconButton>
      </Grid>

      <Grid item xs="auto">
        <DatePicker
          className={styles.date}
          label="開始日"
          value={store.startAt}
          maxDate={store.endAt}
          onChange={handleStartAt}
        />
        <DatePicker
          label="終了日"
          value={store.endAt}
          minDate={store.startAt}
          maxDate={subDays(new Date(), 1)}
          onChange={handleEndAt}
        />
      </Grid>

      <Grid item xs={12}>
        <Table
          title=""
          isLoading={store.loading}
          data={data}
          options={{
            exportFileName: "継続数レポート",
            paging: false,
            search: false,
          }}
          columns={[
            { title: "日付", field: "date", type: "date" },
            { title: "UU", field: "0", sorting: false, type: "numeric" },
            {
              title: "1日後",
              field: "1",
              sorting: false,
              type: "numeric",
              emptyValue: "-",
            },
            {
              title: "3日後",
              field: "3",
              sorting: false,
              type: "numeric",
              emptyValue: "-",
            },
            {
              title: "7日後",
              field: "7",
              sorting: false,
              type: "numeric",
              emptyValue: "-",
            },
            {
              title: "14日後",
              field: "14",
              sorting: false,
              type: "numeric",
              emptyValue: "-",
            },
            {
              title: "30日後",
              field: "30",
              sorting: false,
              type: "numeric",
              emptyValue: "-",
            },
          ]}
        />
      </Grid>
    </Grid>
  ));
};

export default () => (
  <StoreProvider>
    <Page />
  </StoreProvider>
);
